"use client";

import posthog from "posthog-js";

export type AuthMethod =
  | "email"
  | "email_otp"
  | "phone"
  | "google"
  | "facebook"
  | "passkey";
export type AuthStep =
  | "identifier"
  | "signup"
  | "password"
  | "otp"
  | "otp_resend"
  | "oauth"
  | "post_signup_login";
type FunnelEvent =
  | "auth_step_viewed"
  | "auth_step_submitted"
  | "auth_step_succeeded"
  | "auth_step_failed"
  | "auth_validation_failed"
  | "signup_account_created"
  | "signup_completed"
  | "auth_completed"
  | "app_entered";

type FunnelProperties = {
  step_id?: AuthStep;
  auth_method?: AuthMethod;
  intent?: "signup" | "login" | "unknown";
  failure_code?: string;
  duration_ms?: number;
  workspace_id?: string;
  field_id?: string;
};

const FAILURE_CODES = new Set([
  "invalid_input",
  "email_taken",
  "phone_taken",
  "voip",
  "rate_limited",
  "send_failed",
  "invalid_payload",
  "create_failed",
  "mismatch",
  "expired",
  "locked",
  "account_exists",
  "network_error",
  "operation_failed",
  "unexpected_error",
]);
const FIELD_IDS = new Set([
  "identifier",
  "email",
  "password",
  "firstName",
  "lastName",
  "phoneNumber",
  "phoneCountry",
]);

// Deliberately do not forward error text, identifiers, form values, or OTPs.
export function trackFunnelEvent(
  name: FunnelEvent,
  props: FunnelProperties = {},
): void {
  try {
    const {
      step_id,
      auth_method,
      intent,
      duration_ms,
      workspace_id,
      failure_code,
      field_id,
    } = props;
    posthog.capture(name, {
      ...(step_id ? { step_id } : {}),
      ...(auth_method ? { auth_method } : {}),
      ...(intent ? { intent } : {}),
      ...(workspace_id ? { workspace_id } : {}),
      ...(field_id && FIELD_IDS.has(field_id) ? { field_id } : {}),
      ...(duration_ms !== undefined ? { duration_ms } : {}),
      ...(failure_code
        ? {
            failure_code: FAILURE_CODES.has(failure_code)
              ? failure_code
              : "operation_failed",
          }
        : {}),
    });
  } catch {
    // Analytics must never change an auth result or block navigation.
  }
}

export function trackAuthValidation(step: AuthStep, errors: object): void {
  for (const field of Object.keys(errors)) {
    if (FIELD_IDS.has(field)) {
      trackFunnelEvent("auth_validation_failed", {
        step_id: step,
        field_id: field,
        failure_code: "invalid_input",
      });
    }
  }
}

export async function measureAuthStep<
  T extends { success: boolean; failureCode?: string },
>(
  context: Pick<FunnelProperties, "step_id" | "auth_method" | "intent">,
  action: () => Promise<T>,
): Promise<T> {
  const started = Date.now();
  trackFunnelEvent("auth_step_submitted", context);
  try {
    const result = await action();
    trackFunnelEvent(
      result.success ? "auth_step_succeeded" : "auth_step_failed",
      {
        ...context,
        duration_ms: Math.max(0, Date.now() - started),
        ...(!result.success
          ? { failure_code: result.failureCode ?? "operation_failed" }
          : {}),
      },
    );
    return result;
  } catch (error) {
    trackFunnelEvent("auth_step_failed", {
      ...context,
      failure_code: "unexpected_error",
      duration_ms: Math.max(0, Date.now() - started),
    });
    throw error;
  }
}

const SIGNUP_ATTEMPT_KEY = "atriam:signup-attempt:v2";
const SIGNUP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
type SignupAttempt = { startedAt: number; method: AuthMethod };
let memoryAttempt: SignupAttempt | null = null;
const capturedUsers = new Set<string>();

export function beginSignupAttempt(method: AuthMethod): void {
  memoryAttempt = { startedAt: Date.now(), method };
  try {
    window.localStorage.setItem(
      SIGNUP_ATTEMPT_KEY,
      JSON.stringify(memoryAttempt),
    );
  } catch {
    // Same-page tracking still works with storage disabled.
  }
}

export function clearSignupAttempt(): void {
  memoryAttempt = null;
  try {
    window.localStorage.removeItem(SIGNUP_ATTEMPT_KEY);
  } catch {
    // Storage availability must not affect authentication.
  }
}

function readSignupAttempt(): SignupAttempt | null {
  try {
    const raw = window.localStorage.getItem(SIGNUP_ATTEMPT_KEY);
    if (!raw) return memoryAttempt;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const attempt = parsed as Partial<SignupAttempt>;
    if (
      typeof attempt.startedAt !== "number" ||
      !Number.isFinite(attempt.startedAt) ||
      !["email", "email_otp", "google", "facebook"].includes(
        attempt.method ?? "",
      )
    )
      return null;
    return attempt as SignupAttempt;
  } catch {
    return memoryAttempt;
  }
}

/** Only an account created during this browser's signup attempt qualifies.
 * Returning OAuth logins cannot become signups. The attempt survives OAuth,
 * hard navigation, and a delayed retry after account creation/sign-in failure.
 * Identify the verified auth user BEFORE calling this function.
 */
export function captureCompletedSignup(user: {
  id: string;
  created_at: string;
}): boolean {
  const attempt = readSignupAttempt();
  if (!attempt || !posthog.__loaded) return false;
  const createdAt = Date.parse(user.created_at);
  const now = Date.now();
  const validAttempt =
    now - attempt.startedAt <= SIGNUP_MAX_AGE_MS &&
    attempt.startedAt <= now + 10_000;
  if (validAttempt) {
    trackFunnelEvent("auth_completed", {
      auth_method: attempt.method,
      intent:
        Number.isFinite(createdAt) &&
        createdAt >= attempt.startedAt - 10_000 &&
        createdAt <= now + 10_000
          ? "signup"
          : "login",
    });
  }
  // Allow 10 seconds for client/server clock skew; never infer from account age alone.
  if (
    !Number.isFinite(createdAt) ||
    createdAt < attempt.startedAt - 10_000 ||
    createdAt > now + 10_000 ||
    now - attempt.startedAt > SIGNUP_MAX_AGE_MS ||
    attempt.startedAt > now + 10_000
  ) {
    clearSignupAttempt();
    return false;
  }
  const key = `atriam:signup-completed:v2:${user.id}`;
  let alreadyCaptured = capturedUsers.has(user.id);
  try {
    alreadyCaptured ||= window.localStorage.getItem(key) === "1";
  } catch {
    // In-memory deduplication still protects repeated auth callbacks.
  }
  clearSignupAttempt();
  if (alreadyCaptured) return false;
  capturedUsers.add(user.id);
  trackFunnelEvent("signup_completed", {
    auth_method: attempt.method,
    intent: "signup",
  });
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // No impact on the successful signup.
  }
  return true;
}
